import { useState } from "react";
import Info from "@mui/icons-material/Info";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip as MUITooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { TooltipProps as MUITooltipProps } from "@mui/material/Tooltip";
import { tooltipClasses } from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

import type { TranslationKeyAndOptions } from "@thalamos/vision-common";

import { Translation } from "../Translation/Translation.js";

type TooltipProps = {
  fieldName: string;
  title: TranslationKeyAndOptions;
  content: TranslationKeyAndOptions;
};

const IconButtonCircle = ({ onClick }: { onClick: () => void }) => {
  return (
    <InfoOutlined
      data-testid="info-outlined"
      sx={{ color: "white" }}
      onClick={onClick}
    >
      <Info />
    </InfoOutlined>
  );
};

const LightTooltip = styled(({ className, ...props }: MUITooltipProps) => (
  <MUITooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "#49454F",
    boxShadow: theme.shadows[1],
    fontWeight: 400,
    fontSize: "0.875rem",
    maxWidth: 200,
  },
}));

export const Tooltip = ({ fieldName, title, content }: TooltipProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const handleOpen = () => {
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const iconWrapperStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "text.primary",
    borderRadius: "50%",
    cursor: "pointer",
    width: "30px",
    height: "30px",
    "&:hover": {
      backgroundColor: "text.secondary",
    },
  };

  return isMobile ? (
    <Box data-testid={`tooltip-${fieldName}-mobile`}>
      <Box sx={iconWrapperStyles}>
        <IconButtonCircle onClick={handleOpen} />
      </Box>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle sx={{ fontSize: "0.875rem" }}>
          <Translation {...title} />
        </DialogTitle>
        <DialogContent sx={{ fontSize: "0.875rem" }}>
          <Translation {...content} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start" }}>
          <Button
            data-testid="popup-close-dialog"
            onClick={handleClose}
            color="primary"
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  ) : (
    <LightTooltip
      data-testid={`tooltip-${fieldName}-desktop`}
      title={
        <Box>
          <Box sx={{ fontWeight: "bold", mb: 1 }}>
            <Translation {...title} />
          </Box>
          <Box>
            <Translation {...content} />
          </Box>
        </Box>
      }
      placement="right-start"
    >
      <Box sx={iconWrapperStyles}>
        <InfoOutlined
          data-testid="info-outlined"
          sx={{ color: "white" }}
          onClick={handleOpen}
        />
      </Box>
    </LightTooltip>
  );
};
